import type { RegistryDatasetRef } from "@/api/training";
import type { TrainingEvalRunSummaryView, TrainingEvalScenarioView } from "@/api/trainingEval";
import type { TrainingBudgetUnit } from "@/api/trainingPolicies";
import { formatSuccessRate, isSucceededEvalStatus } from "@/modules/evaluation/public";
import { registryDatasetDisplayLabel, type CheckpointTag } from "@/modules/training/public";
import { TableRow } from "@carbon6/c6-frontend-design";
import { budgetUnitCellLabel, checkpointCellText } from "@/components/table/CheckpointCount";
import { formatBranchHash } from "@/components/table/branchHash";
import {
  runEpisodes,
  scenarioEnvironmentCount,
  scenarioParams,
  simulationSeed,
} from "./evalRunScenarioModel";
import {
  evalRunPolicyLabel,
  evalRunSessionLabel,
  formatEvalDateTime,
  formatEvalDuration,
  orEmptyCell,
} from "./evalRunFormat";
import { parseUtcMs } from "@/utils/datetime";

export type EvalRunRowClickHandler = (run: TrainingEvalRunSummaryView) => void;

// Evaluation 결과 행이다. DataTable 열 키에 맞춘 평평한 표시 필드를 가진다.
// 소요 시간은 행을 만들 때 고정한다. 패널은 startedAtMs 로 상대 시각을 로컬에서 갱신한다.
export class EvalRunTableRow extends TableRow {
  readonly id: string;
  // 첫 열에 표시하는 이름이다. 행이 가리키는 대상은 세션이 아니라 그 세션이 만든 정책이다.
  // 목록에 정책 이름이 없으면 세션 이름으로 대체해 칸을 비우지 않는다.
  readonly policyLabel: string;
  // 표에 표시하지 않는다. 첫 열 링크가 여는 세션 창의 이름 seed 이고, policyLabel 의 폴백이다.
  readonly sessionLabel: string;
  // 평가한 checkpoint 수다. 단위는 budgetUnit 이다. "#" 셀과 "UNIT" 셀 한 쌍에 쓴다.
  readonly checkpointLabel: string;
  readonly checkpointValue: number | null;
  readonly budgetUnit: TrainingBudgetUnit;
  // 평가한 체크포인트(단위+번호)다. 첫 열 링크가 세션 창의 체크포인트 탭에서 그 행을 가리킬 때
  // 전달한다. 번호를 모르면 null 이고, 링크는 탭만 연다.
  readonly checkpointTag: CheckpointTag | null;
  readonly unitLabel: string;
  // "#" 셀의 hover 에 표시하는 세션 전체 값이다. 셀 자체는 checkpoint 수를 표시한다.
  readonly sessionEpochValue: number | null;
  readonly sessionStepValue: number | null;
  // 코드 출처다. 라벨은 정렬과 잘림 툴팁이 쓰는 문자열이다. 셀은 원본 두 부분을 표시하고 구분자만 강조한다.
  readonly branchHashLabel: string;
  readonly branch: string | null;
  readonly shortHash: string | null;
  readonly datasetLabel: string;
  // 열 렌더러가 dataset 셀의 링크를 만들 때 쓰는 registry 출처다.
  readonly datasetRef: RegistryDatasetRef | null;
  readonly taskLabel: string;
  readonly dockerTagLabel: string;
  readonly startedLabel: string;
  readonly startedAtMs: number | null;
  readonly durationLabel: string;
  // 실행 조건이다. 목록에 없으므로 scenario 카탈로그에서 이름으로 조인한다.
  // scenario 를 찾지 못하면 빈 칸으로 두고 나머지 열은 그대로 표시한다.
  //
  // 상세 창은 같은 값을 run 의 params 에서 읽고 scenario 값을 덮어쓴다(`scenarioParameterRows`).
  // 목록 응답에는 params 가 없다. 그래서 여기서는 scenario 가 선언한 값을 쓴다. rerun 이
  // override_params 로 seed 를 덮어쓴 run 은 두 화면의 값이 다를 수 있다. 그때는 상세가 기준이다.
  readonly seedLabel: string;
  readonly numEnvsLabel: string;
  readonly episodesLabel: string;
  readonly successRateLabel: string;
  readonly status: string;
  readonly sourceSessionId: string | null;
  private readonly run: TrainingEvalRunSummaryView;
  private readonly clickHandler: EvalRunRowClickHandler;

  constructor(
    run: TrainingEvalRunSummaryView,
    clickHandler: EvalRunRowClickHandler,
    nowMs: number = Date.now(),
    scenario: TrainingEvalScenarioView | null = null,
  ) {
    super(null);
    const datasetRef = run.dataset_registry_ref ?? null;
    this.run = run;
    this.clickHandler = clickHandler;
    this.id = run.id;
    this.sessionLabel = evalRunSessionLabel(run);
    this.policyLabel = evalRunPolicyLabel(run);
    // 평가한 체크포인트와 세션 전체 학습량은 서로 다른 값이므로 별도 필드로 유지한다.
    this.checkpointValue = run.epoch;
    this.budgetUnit = run.budget_unit;
    this.unitLabel = budgetUnitCellLabel(this.budgetUnit);
    this.checkpointLabel = checkpointCellText(this.checkpointValue, this.budgetUnit);
    this.checkpointTag =
      this.checkpointValue !== null ? { unit: this.budgetUnit, value: this.checkpointValue } : null;
    this.sessionEpochValue = run.source_session_total_epochs;
    this.sessionStepValue = run.source_session_total_steps;
    this.branch = run.branch;
    this.shortHash = run.git_short_hash;
    this.branchHashLabel = formatBranchHash(this.branch, this.shortHash);
    this.datasetRef = datasetRef;
    this.datasetLabel = datasetCellLabel(run, datasetRef);
    this.taskLabel = orEmptyCell(run.task);
    this.dockerTagLabel = orEmptyCell(run.docker_tag);
    const startedAt = run.started_at ?? run.created_at;
    this.startedAtMs = parseUtcMs(startedAt);
    this.startedLabel = formatEvalDateTime(startedAt);
    this.durationLabel = formatEvalDuration(run.started_at, run.finished_at, nowMs);
    const params = scenarioParams(scenario);
    this.seedLabel = orEmptyCell(simulationSeed(params));
    this.numEnvsLabel = orEmptyCell(scenarioEnvironmentCount(scenario)?.toString() ?? null);
    this.episodesLabel = orEmptyCell(runEpisodes(params));
    this.successRateLabel = formatSuccessRate(
      run.success_rate ?? run.progress_success_rate,
      run.success_rate_state,
    );
    this.status = run.status;
    this.sourceSessionId = run.source_session_id;
  }

  getRowId(): string {
    return this.id;
  }

  // 목록 응답의 요약을 상세·비교 화면에 그대로 전달한다.
  getRun(): TrainingEvalRunSummaryView {
    return this.run;
  }

  // 서버 필터가 표시 여부를 정한다. 그래서 반환된 행은 모두 표시한다.
  canShow(): boolean {
    return true;
  }

  // 비교에는 완료된 결과가 필요하다. 그래서 succeeded run 만 선택할 수 있다.
  canSelect(): boolean {
    return isSucceededEvalStatus(this.status);
  }

  override get isRowClickable(): boolean {
    return true;
  }

  override onRowClick(): void {
    this.clickHandler(this.run);
  }
}

// 경우는 registry 표시명, 상류 이름, training-infra 전용 표기, 입력 없음(대시)이다. 앞 세 경우의
// 판정은 Training module 공개 계약이 소유한다.
function datasetCellLabel(
  run: TrainingEvalRunSummaryView,
  datasetRef: RegistryDatasetRef | null,
): string {
  if (!run.upstream_dataset_label && !datasetRef) {
    return "—";
  }
  return registryDatasetDisplayLabel(datasetRef, run.upstream_dataset_label);
}
